import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill } from 'react-quill';
import { styled } from '@mui/material';

import Accordion from '~/components/ui/Accordion/Accordion';
import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';

import 'react-quill/dist/quill.snow.css';

const modules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const MAX_EDITOR_LENGTH = 500;

const formats = [
  'header',
  'font',
  'bold',
  'italic',
  'underline',
  'strike',
  'direction',
  'blockquote',
  'list',
  'bullet',
  'link',
];

type VCardPlusSummaryProps = {
  defaultValue?: string;
  updateVCardPlusData?: () => void;
  hideAccordionBottomBorder?: boolean;
};

export default function VCardPlusSummary({
  defaultValue,
  updateVCardPlusData,
  hideAccordionBottomBorder = false,
}: VCardPlusSummaryProps) {
  const { t } = useTranslation();

  const [currChar, setCurrChar] = useState(null);
  const [value, setValue] = useState(defaultValue);

  Quill.register('modules/maxlength', function (quill, options) {
    quill.on('text-change', function () {
      const textLength = quill.getText().length;
      setCurrChar(textLength - 1);
      if (textLength > options.maxLength) {
        quill.deleteText(options.maxLength, textLength - options.maxLength);
      }
    });
  });

  const handleBlur = () => {
    updateVCardPlusData({ summary: value });
  };

  return (
    <Accordion
      defaultExpanded
      variant="child"
      hideBottomBorder={hideAccordionBottomBorder}
      summary={t('template5:summary')}
      details={
        <>
          <StyledReactQuill
            theme="snow"
            value={value}
            onChange={setValue}
            onBlur={handleBlur}
            modules={{ ...modules, maxlength: { maxLength: MAX_EDITOR_LENGTH } }}
            formats={formats}
          />
          <Text variant="inputVerySmall" sx={{ textAlign: 'right', color: COLORS.black06 }}>
            {value ? currChar : 0}/{MAX_EDITOR_LENGTH}
          </Text>
        </>
      }
    />
  );
}

const StyledReactQuill = styled(ReactQuill)(() => {
  return {
    marginBottom: '12px',
    wordBreak: 'break-word',
    backgroundColor: COLORS.white,
    '.ql-editor': {
      height: '200px',
    },
  };
});
